import React from "react";

import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

import { displayEdit, buildEmptyObject } from "@auto/widgets/EditComponent";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./VoterForm.module.styl";

const RELATED_LIMIT = 5000;

const getField = (row, camelName, snakeName) => {
  return row?.[camelName] ?? row?.[snakeName] ?? "";
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.replace("T", " ").slice(0, 19);
  }

  try {
    return new Date(value).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return String(value);
  }
};

const formatStatusCode = (value) => {
  const raw =
    value === null || value === undefined
      ? ""
      : String(value).trim().toUpperCase();

  switch (raw) {
    case "1":
    case "SUBMITTED":
      return t("common.submitted");

    case "3":
    case "APPROVED":
      return t("common.approved");

    case "4":
    case "MANUAL_ADJUDICATION":
    case "MANUAL ADJUDICATION":
      return t("voter.manualAdjudication");

    default:
      return raw;
  }
};

const search = async (entity, payload) => {
  return await rest.request(
    getServiceUri() + `${entity}/search`,
    "POST",
    payload
  );
};

const searchOneByGid = async (entity, gid) => {
  if (!gid) {
    return null;
  }

  const rows = await search(entity, {
    columns: [],
    query: `gid==${gid}`,
    limit: 1,
    offset: 0,
  });

  return rows?.[0] || null;
};

const readByGid = async (entity, gid) => {
  if (!gid) {
    return null;
  }

  return await rest.request(getServiceUri() + `${entity}/${gid}`, "GET");
};

const readPersonByGid = async (gid) => {
  return await searchOneByGid("person", gid);
};

const searchByPersonGid = async (entity, personGid) => {
  if (!personGid) {
    return [];
  }

  return await search(entity, {
    columns: [],
    query: `personGid==${personGid}`,
    limit: RELATED_LIMIT,
    offset: 0,
  });
};

const pickPersonName = (person) => {
  if (!person) {
    return "";
  }

  const firstName = getField(person, "firstName", "first_name");
  const lastName = getField(person, "lastName", "last_name");
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || getField(person, "nationalIdentifier", "national_identifier");
};

const pickVotingCenterLabel = (votingCenter) => {
  if (!votingCenter) {
    return "";
  }

  return votingCenter.name || votingCenter.code || "";
};

const isDefaultPhoto = (photo) => {
  return photo?.isDefault === true || photo?.is_default === true;
};

const pickPrimaryPhoto = (photos) => {
  if (!photos || photos.length === 0) {
    return null;
  }

  return photos.find(isDefaultPhoto) || photos[0];
};

const personPhotoSrc = (person) => {
  const defaultImageId = getField(person, "defaultImageId", "default_image_id");

  if (!defaultImageId) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${Number(defaultImageId)}`;
};

const enrichApplications = (applications) => {
  return (applications || []).map((application) => ({
    ...application,
    gid: application.gid ?? "",
    receiptNumber: getField(application, "receiptNumber", "receipt_number"),
    submittedAtFormatted: formatDateTime(getField(application, "submittedAt", "submitted_at")),
    reviewedAtFormatted: formatDateTime(getField(application, "reviewedAt", "reviewed_at")),
    statusCodeFormatted: formatStatusCode(getField(application, "statusCode", "status_code")),
  }));
};

const enrichVoter = async (voter, gui) => {
  if (!voter) {
    return {};
  }

  const personGid = getField(voter, "personGid", "person_gid");
  const votingCenterGid = getField(voter, "votingCenterGid", "voting_center_gid");

  const [person, votingCenter, applications] = await Promise.all([
    personGid && can(gui, "READ_PERSON")
      ? readPersonByGid(personGid)
      : Promise.resolve(null),
    votingCenterGid ? readByGid("voting-center", votingCenterGid) : Promise.resolve(null),
    personGid && can(gui, "SEARCH_VOTER_APPLICATION")
      ? searchByPersonGid("voter-application", personGid)
      : Promise.resolve([]),
  ]);

  return {
    ...voter,

    personGid,
    votingCenterGid,

    person,
    votingCenter,
    personName: pickPersonName(person),
    personIdentifier: getField(person, "nationalIdentifier", "national_identifier"),
    personPhotoSrc: can(gui, "READ_PERSON_PHOTO") ? personPhotoSrc(person) : "",

    votingCenterLabel: pickVotingCenterLabel(votingCenter),
    votingCenterName: votingCenter?.name || "",
    votingCenterAddress: votingCenter?.address || "",

    applications: enrichApplications(applications),
  };
};

const SectionCard = ({ icon, title, subtitle, children }) => {
  const Icon = icon;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitleRow}>
          <div className={styles.cardIconWrap}>
            <Icon className={styles.cardIcon} />
          </div>

          <div className={styles.cardHeaderText}>
            <h2 className={styles.cardTitle}>{title}</h2>
            {subtitle ? <div className={styles.cardSubtitle}>{subtitle}</div> : null}
          </div>
        </div>
      </header>

      <div className={styles.cardBody}>{children}</div>
    </section>
  );
};

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{String(value)}</div>
    </div>
  );
};

const EmptyCollection = ({ label }) => {
  return (
    <div className={styles.field}>
      <div className={styles.fieldValue}>{t(label)}</div>
    </div>
  );
};

const PersonCardBody = ({ data }) => {
  return (
    <div className={styles.personBlock}>
      <div className={styles.personPhotoWrap}>
        {data.personPhotoSrc ? (
          <img
            src={data.personPhotoSrc}
            alt={data.personName || t("person.photoAlt")}
            className={styles.personPhoto}
          />
        ) : (
          <div className={styles.noPhoto}>{t("voter.noPhoto")}</div>
        )}
      </div>

      <div className={styles.personFields}>
        <div className={styles.fieldsGrid}>
          <Field label={t("common.person")} value={data.personName} />
          <Field label={t("person.nationalId")} value={data.personIdentifier} />
        </div>
      </div>
    </div>
  );
};

const ApplicationsCardBody = ({ applications }) => {
  if (!applications || applications.length === 0) {
    return <EmptyCollection label="person.noApplications" />;
  }

  return (
    <div className={styles.fieldsGridWide}>
      {applications.map((application, index) => {
        const title =
          application.receiptNumber ||
          application.statusCodeFormatted ||
          `${t("common.application")} ${index + 1}`;

        return (
          <React.Fragment key={application.gid || index}>
            <Field label={t("common.application")} value={title} />
            <Field label={t("common.status")} value={application.statusCodeFormatted} />
            <Field label={t("voter.submittedAt")} value={application.submittedAtFormatted} />
            <Field label={t("voter.reviewedAt")} value={application.reviewedAtFormatted} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

const VoterHeader = () => {
  return (
    <section className={styles.header}>
      <div className={styles.headerMain}>
        <div className={styles.heroIconWrap}>
          <HowToVoteOutlinedIcon className={styles.heroIcon} />
        </div>

        <div className={styles.headerText}>
          <h1 className={styles.headerTitle}>{t("voter.record")}</h1>
          <div className={styles.headerSubtitle}>{t("voter.record.subtitle")}</div>
        </div>
      </div>
    </section>
  );
};

const VoterReadView = ({ gui, id }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setError("");

        const voter = await readByGid("voter", id);
        const enriched = await enrichVoter(voter, gui);

        if (!cancelled) {
          setData(enriched);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || String(err));
          setData(null);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [gui, id]);

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!data) {
    return <div className={styles.loading}>{t("common.loading")}</div>;
  }

  return (
    <div className={styles.page}>
      <VoterHeader data={data} />

      <div className={styles.gridTwo}>
        <SectionCard
          icon={PersonOutlineOutlinedIcon}
          title={t("common.person")}
          subtitle={t("voter.registeredCitizenIdentity")}
        >
          <PersonCardBody data={data} />
        </SectionCard>

        <SectionCard
          icon={PlaceOutlinedIcon}
          title={t("voter.votingCenter")}
          subtitle={t("voter.assignedVotingLocation")}
        >
          <div className={styles.fieldsGrid}>
            <Field label={t("voter.votingCenter")} value={data.votingCenterLabel} />
            <Field label={t("common.address")} value={data.votingCenterAddress} />
          </div>
        </SectionCard>
      </div>

      {can(gui, "SEARCH_VOTER_APPLICATION") ? (
        <SectionCard
          icon={ReceiptLongOutlinedIcon}
          title={t("person.applications")}
          subtitle={t("voter.applicationsLinked")}
        >
          <ApplicationsCardBody applications={data.applications} />
        </SectionCard>
      ) : null}
    </div>
  );
};

export const voterProjection = {
  sections: [
    {
      title: "voter.voter",
      fields: [
        { name: "personGid", label: "person.personGid", valueType: "number", inputType: "number", span: 6 },
        { name: "votingCenterGid", label: "voter.votingCenterGid", valueType: "number", inputType: "number", span: 6 },
      ],
    },
  ],
};

export const displayReadVoter = (gui, id) => {
  return {
    uuid: `voter-read-${id}`,
    view: () => <VoterReadView gui={gui} id={id} />,
  };
};

export const displayEditVoter = (gui, id, onFinish) => {
  return displayEdit({
    gui,
    projection: voterProjection,

    loadData: async () => {
      if (!id) {
        return buildEmptyObject(voterProjection);
      }

      const voter = await readByGid("voter", id);
      return await enrichVoter(voter, gui);
    },

    onSubmit: async (values) => {
      const payload = { ...values };

      delete payload.person;
      delete payload.votingCenter;
      delete payload.applications;

      delete payload.personName;
      delete payload.personIdentifier;
      delete payload.personPhotoSrc;

      delete payload.votingCenterLabel;
      delete payload.votingCenterName;
      delete payload.votingCenterAddress;

      const response = await rest.request(
        getServiceUri() + (id ? "voter/update-document" : "voter/new-document"),
        "POST",
        payload
      );

      if (onFinish) {
        onFinish(response);
      }

      return response;
    },
  });
};